import { useState, useCallback } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ModeProvider } from '@/providers/ModeProvider';
import { DocumentTemplatesPage } from '@/pages/settings/document-templates';
import { TemplateEditorPage } from '@/pages/settings/template-editor';
import type { Template } from '@/types/template';

type EditorState =
  | { open: false }
  | { open: true; mode: 'create'; template?: Template; duplicateFromId?: string }
  | { open: true; mode: 'edit'; template: Template };

export function Step4Templates() {
  const [setupNow, setSetupNow] = useState(false);
  const [editorState, setEditorState] = useState<EditorState>({ open: false });

  const openCreate = useCallback(() => {
    setEditorState({ open: true, mode: 'create' });
  }, []);

  const openEdit = useCallback((template: Template) => {
    setEditorState({ open: true, mode: 'edit', template });
  }, []);

  const openDuplicate = useCallback((template: Template) => {
    setEditorState({
      open: true,
      mode: 'create',
      template: { ...template, name: `${template.name} (COPY)` },
      duplicateFromId: template.id,
    });
  }, []);

  const closeEditor = useCallback(() => {
    setEditorState({ open: false });
  }, []);

  const handleSaved = useCallback((_savedTemplate: Template, _mode: 'create' | 'edit') => {
    // Return to the templates list inside the wizard
    setEditorState({ open: false });
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Step header — hidden when the inline editor is open */}
      {!editorState.open && (
        <div className="mb-6 shrink-0">
          <h2 className="text-xl font-semibold mb-1">Templates</h2>
          <p className="text-sm text-muted-foreground">
            Review and create document templates for your workflows.
          </p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {editorState.open ? (
          /* Inline template editor — fills the wizard content area */
          <ModeProvider>
            <TemplateEditorPage
              inlineMode={{
                template: editorState.template,
                mode: editorState.mode,
                duplicateFromId: editorState.open && editorState.mode === 'create' ? editorState.duplicateFromId : undefined,
                onBack: closeEditor,
                onSaved: handleSaved,
              }}
            />
          </ModeProvider>
        ) : setupNow ? (
          <ModeProvider>
            <DocumentTemplatesPage
              onCreateTemplate={openCreate}
              onEditTemplate={openEdit}
              onDuplicateTemplate={openDuplicate}
            />
          </ModeProvider>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-6">
              <FileText className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No templates set up yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Templates are <strong>not mandatory</strong> — you can set up the rest of Access
              Collect without them. When you're ready, they can also be created from{' '}
              <strong>Settings → Templates</strong>.
            </p>
            <Button onClick={() => setSetupNow(true)} className="gap-2">
              <FileText className="h-4 w-4" />
              Set Up Templates Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
